import {
  DateRangePicker,
  Field,
  MultiSelectDropdown,
  SearchInput,
  SingleSelectDropdown,
  Switch,
  type DateRangeValue,
} from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { hatcheryDropdownModes } from "@/app/theme";
import { FilterResultChip } from "@/components/filter";
import {
  clearSessionFilterAxis,
  sessionFilterChips,
  type SessionFilterState,
} from "./sessionFilters";
import { SESSION_STARTED_WITHIN_OPTIONS, isSessionStartedWithin } from "./sessionStartedWithin";
import { SUBMIT_STATUS_OPTIONS } from "@/modules/training/public";
import type { SessionFilterOptions } from "./useSessionFilterOptions";
import styles from "./SessionFilterBar.module.css";

// 드롭다운의 "안 고름" 값. c6 단일 선택은 빈 문자열을 중립 값(neutralValue)으로 쓴다.
const NOT_SELECTED = "";
// 시작일 드롭다운의 "직접 지정" 값. 프리셋이 아니라 날짜 선택기를 여는 항목이다.
const STARTED_CUSTOM = "custom";
// 상태 드롭다운의 "취소 제외" 항목 값. 카테고리가 아니라 같은 축의 제외 조건이므로 상태 어휘
// 밖의 값을 쓴다. 상태에서는 `excludeCancelled` 플래그, 서버에서는 `exclude_status_group` 이다.
const EXCLUDE_CANCELLED = "exclude:cancelled";

const STARTED_OPTIONS = [
  { value: NOT_SELECTED, label: "전체" },
  ...SESSION_STARTED_WITHIN_OPTIONS.map(({ value, label }) => ({ value, label })),
  { value: STARTED_CUSTOM, label: "직접 지정", description: "시작일과 종료일을 고릅니다" },
];

type SessionFilterBarProps = {
  filters: SessionFilterState;
  onChange: (next: SessionFilterState) => void;
  options: SessionFilterOptions;
  showMemberFilter: boolean;
  // 검색 바로 뒤에 넣는 팀 그룹 컨트롤(페이지가 소유한다). 스코프(My/Team)는 제목 줄에 있다.
  leadingControls?: ReactNode;
};

/**
 * 세션 목록 조회 축. 두 줄 컨트롤과 적용 조건 칩이다.
 *
 * 상태는 URL 이 소유한다. 패널이 상태를 읽어 전달하고, 이 컴포넌트는 표시와 변경 이벤트만
 * 맡는다. 컨트롤에서 고른 값과 아래 칩은 같은 상태의 두 표현이다. 그래서 칩 ✕ 로 해제한 조건은
 * 컨트롤에서도 바로 해제된다.
 *
 * 브랜치와 데이터셋은 자유 입력이 아니라 카탈로그에서 고른다. 서버 매칭이 부분 일치라서 오타가
 * 알림 없이 빈 목록을 만들기 때문이다. 해시 축은 컨트롤이 없고 URL(`?hash=`)로만 받는다.
 * 브랜치 드롭다운의 검색이 sha 로도 일치하므로 화면에 두 칸을 둘 이유가 없다.
 */
export function SessionFilterBar({
  filters,
  onChange,
  options,
  showMemberFilter,
  leadingControls,
}: SessionFilterBarProps): ReactNode {
  // 다중 선택에서 전체 상태(빈 값)는 뜻이 없다. 아무것도 고르지 않은 상태가 전체다.
  const statusOptions = useMemo(
    () => SUBMIT_STATUS_OPTIONS.filter((option) => option.value !== NOT_SELECTED),
    [],
  );
  const statusLabelByKey = useMemo(
    () => Object.fromEntries(statusOptions.map((option) => [option.value, option.label])),
    [statusOptions],
  );
  // 취소 제외는 상태 축의 제외 조건이다. 그래서 따로 스위치를 두지 않고 같은 드롭다운의 마지막
  // 항목으로 둔다. 서버는 포함(카테고리)과 제외를 한 요청에 받지 않는다(422). 그래서 한쪽을 고른
  // 동안 다른 쪽을 잠근다. 드롭다운에 보이는 선택이 서버로 보내는 조건과 같다.
  const hasStatusGroups = filters.statusGroups.length > 0;
  const excludeSelected = filters.excludeCancelled && !hasStatusGroups;
  const statusMenuOptions = useMemo(
    () => [
      ...statusOptions.map((option) => ({ ...option, disabled: excludeSelected })),
      {
        value: EXCLUDE_CANCELLED,
        label: "취소 제외",
        description: "취소만 빼고 전부 봅니다. 카테고리와 함께 고를 수 없습니다.",
        disabled: hasStatusGroups,
      },
    ],
    [statusOptions, excludeSelected, hasStatusGroups],
  );
  const statusSelection = excludeSelected ? [EXCLUDE_CANCELLED] : [...filters.statusGroups];
  // 검색 초안. 확정 전 타이핑은 URL 로 보내지 않는다. 밖에서 값이 바뀌면(칩 ✕·딥링크)
  // 그 값을 따른다.
  const [searchDraft, setSearchDraft] = useState(filters.search);
  useEffect(() => setSearchDraft(filters.search), [filters.search]);

  const dateRange = useMemo<DateRangeValue>(
    () => ({ start: parseDate(filters.startedAfter), end: parseDate(filters.startedBefore) }),
    [filters.startedAfter, filters.startedBefore],
  );
  const hasCustomRange = filters.startedAfter !== "" || filters.startedBefore !== "";
  // "직접 지정"을 고른 직후에는 날짜가 아직 없다. 선택기를 표시하려면 그 의도를 따로 보관한다.
  // 날짜를 적용하거나 프리셋으로 바꾸면 이 값은 뜻이 없다.
  const [customPicking, setCustomPicking] = useState(false);
  const startedValue = hasCustomRange || customPicking ? STARTED_CUSTOM : filters.startedWithin;

  const chips = useMemo(
    () =>
      sessionFilterChips(filters, {
        memberNameById: options.memberNameById,
        experimentNameById: options.experimentNameById,
        datasetNameByUuid: options.datasetNameByUuid,
        statusLabelByKey,
      }),
    [
      filters,
      options.memberNameById,
      options.experimentNameById,
      options.datasetNameByUuid,
      statusLabelByKey,
    ],
  );

  return (
    <div className={styles.bar}>
      <div className={styles.row}>
        {/* 데이터셋 검색창과 같은 컨트롤(c6 SearchInput)이다. 타이핑은 로컬에 두고 Enter 나
            검색 버튼에서만 조건을 적용한다. 글자마다 URL 을 바꾸면 요청이 타이핑 수만큼 나간다.
            서버 축이 아직 없어서(ZERG-312) 지금은 목록을 좁히지 못한다. 힌트가 그 사실을
            표시한다. 축이 생기면 "(준비 중)" 만 제거한다. */}
        <div className={styles.searchBox} role="search">
          <SearchInput
            value={searchDraft}
            placeholder="이름·노트 검색 (준비 중)"
            aria-label="이름·노트 검색"
            showClear
            onValueChange={(value) => {
              setSearchDraft(value);
              // 비우기(✕)는 조건 해제로 본다. c6 는 비울 때 onSearch 를 호출하지 않는다. 여기서
              // 연결하지 않으면 지운 뒤에도 목록이 계속 좁혀져 있다.
              if (value === "" && filters.search !== "") {
                onChange({ ...filters, search: "" });
              }
            }}
            onSearch={() => onChange({ ...filters, search: searchDraft.trim() })}
          />
        </div>
        {leadingControls}
        {showMemberFilter ? (
          <SingleSelectDropdown
            mode={hatcheryDropdownModes.filter}
            label="멤버"
            neutralValue={NOT_SELECTED}
            searchable
            value={filters.memberUuid ?? NOT_SELECTED}
            options={[{ value: NOT_SELECTED, label: "전체" }, ...options.memberOptions]}
            onValueChange={(next) =>
              onChange({
                ...filters,
                memberUuid: next === NOT_SELECTED ? null : next,
                experimentId: null,
              })
            }
          />
        ) : null}
        <MultiSelectDropdown
          mode={hatcheryDropdownModes.filter}
          label="상태"
          options={statusMenuOptions}
          selectedValues={statusSelection}
          onSelectionChange={(next) =>
            onChange({
              ...filters,
              statusGroups: next.filter((value) => value !== EXCLUDE_CANCELLED),
              excludeCancelled: next.includes(EXCLUDE_CANCELLED),
            })
          }
        />
      </div>
      <div className={styles.row}>
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.filter}
          className={styles.axisSelect}
          label="실험"
          neutralValue={NOT_SELECTED}
          searchable
          value={filters.experimentId ?? NOT_SELECTED}
          options={[{ value: NOT_SELECTED, label: "전체" }, ...options.experimentOptions]}
          onValueChange={(next) =>
            onChange({ ...filters, experimentId: next === NOT_SELECTED ? null : next })
          }
        />
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.filter}
          className={styles.axisSelect}
          label="브랜치"
          neutralValue={NOT_SELECTED}
          searchable
          searchPlaceholder="브랜치 또는 해시"
          value={filters.branch}
          options={[{ value: NOT_SELECTED, label: "전체" }, ...options.branchOptions]}
          onValueChange={(next) => onChange({ ...filters, branch: next })}
        />
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.filter}
          className={styles.axisSelect}
          label="Dataset"
          neutralValue={NOT_SELECTED}
          searchable
          searchPlaceholder="Dataset 이름"
          value={filters.dataset}
          options={[{ value: NOT_SELECTED, label: "전체" }, ...options.datasetOptions]}
          onValueChange={(next) => onChange({ ...filters, dataset: next })}
        />
        {/* 표의 "생성일"(제출 시각)이 아니라 학습이 실제로 시작된 날이다. 자리를 기다린
            세션은 두 날이 다르다. 프리셋("최근 1주")은 URL 에 키로 남고, 요청할 때 절대 날짜로
            바꾼다(sessionStartedWithin.ts). "직접 지정"을 고르면 옆에 날짜 선택기를 표시한다.
            상류에 축이 없어서(ZERG-312) 지금은 좁히지 못한다. 라벨이 그 사실을 표시한다.
            축이 생기면 "(준비 중)" 만 제거한다. */}
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.filter}
          label="Training 시작일 (준비 중)"
          neutralValue={NOT_SELECTED}
          value={startedValue}
          options={STARTED_OPTIONS}
          onValueChange={(next) => {
            if (next === STARTED_CUSTOM) {
              setCustomPicking(true);
              onChange({ ...filters, startedWithin: "" });
              return;
            }
            setCustomPicking(false);
            onChange({
              ...filters,
              startedAfter: "",
              startedBefore: "",
              startedWithin: isSessionStartedWithin(next) ? next : "",
            });
          }}
        />
        {startedValue === STARTED_CUSTOM ? (
          <DateRangePicker
            placeholder="시작일 범위"
            value={dateRange}
            onChange={(next) =>
              onChange({
                ...filters,
                startedWithin: "",
                startedAfter: formatDate(next.start),
                startedBefore: formatDate(next.end),
              })
            }
          />
        ) : null}
        {/* 삭제는 즉시 하지 않고 7일 유예한다(상류 archive). 필터 라벨이 그 사실을 표시한다.
            이 축은 상태 축과 독립이고 항상 적용한다. 목록에 **더하는** 축이라 켜도 지금 보이는
            행은 그대로 남는다. */}
        <Field label="삭제 예정 포함" layout="hug" labelPlacement="end">
          <Switch
            checked={filters.includeDeleted}
            onChange={(event) => onChange({ ...filters, includeDeleted: event.target.checked })}
            aria-label="삭제 예정 포함"
          />
        </Field>
      </div>
      {chips.length > 0 ? (
        <div className={styles.row}>
          {chips.map((chip) => (
            <FilterResultChip
              key={`${chip.axis}:${chip.value ?? ""}`}
              label={chip.label}
              removeLabel={`${chip.label} 해제`}
              onRemove={() => onChange(clearSessionFilterAxis(filters, chip.axis, chip.value))}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

// c6 DateRangePicker 는 Date 를 쓰고 서버와 URL 은 YYYY-MM-DD 를 쓴다. 시간대 차이를 피하려고
// 로컬 자정 기준으로만 바꾼다. 날짜 축이라 시각은 뜻이 없다.
function parseDate(value: string): Date | null {
  if (value === "") {
    return null;
  }
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function formatDate(value: Date | null): string {
  if (value === null) {
    return "";
  }
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${value.getFullYear()}-${month}-${day}`;
}
